import axios from "axios";
import React from "react";
import { AiFillHeart, AiOutlineHeart } from "react-icons/ai";
import { BsCartCheck } from "react-icons/bs";
import { FiShoppingCart } from "react-icons/fi";
import { NavLink } from "react-router-dom";
import Swal from "sweetalert2";
import Empty from "../../assets/images/appImages/category.png";
import { useAppDispatch, useAppSelector } from "../../redux/hook";
import {
  addCartProduct,
  getCartCount,
  getTotalAmount,
  removeCartItem,
  type CartItemType,
} from "../../redux/slice/useCartSlice";
import { formatCurrency, getImage, truncateCaractere } from "../../utils/Utils";
import { useLazyFavoriteByUserQuery } from "../../utils/api/favories/favorie.api";
import { useGetVendeurByApiKeyQuery } from "../../utils/api/product/product.api";
import { ApiBaseUrl, VendeurApikey } from "../../utils/http";
import { Color } from "../../utils/theme";
import type { Product } from "../../utils/api/product/product.type";

interface MeilleursProduitsCardProps {
  item: Product;
  onFavoriteToggle?: () => void;
}

/**
 * Carte "Meilleurs produits" pour la bannière d'accueil.
 * Desktop : image à gauche, détails à droite (nom, catégorie, description, prix, Acheter maintenant, Ajouter au panier, favoris).
 * Mobile : image au-dessus, détails en dessous.
 */
export const MeilleursProduitsCard: React.FC<MeilleursProduitsCardProps> = ({
  item,
  onFavoriteToggle,
}) => {
  const dispatch = useAppDispatch();
  const { user, token } = useAppSelector((s) => s?.user);
  const { cartItems } = useAppSelector((s) => s?.cart);
  const { data: vendeur } = useGetVendeurByApiKeyQuery(VendeurApikey);
  const devise = (vendeur?.devise as string) || "XOF";

  const imgProd =
    item?.type === "taille_unique"
      ? item?.images?.[0]?.image
      : item?.variations?.[0]?.images?.[0]?.image ?? null;

  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };

  const [findBySlug, { data: favorisData, isLoading }] =
    useLazyFavoriteByUserQuery();

  React.useEffect(() => {
    if (user?.slug) {
      findBySlug(user?.slug as string);
    }
  }, [user?.slug, findBySlug]);

  const handleAddFavoris = (id: number | string) => {
    const favorie = favorisData?.find((x) => x?.produit?.id == id);
    const fd = new FormData();
    fd.append("user", String(user?.id ?? ""));
    fd.append("produit", String(id));

    if (favorie) {
      axios
        .delete(ApiBaseUrl + `/api/favoris/${favorie?.slug}`, config)
        .then(() => {
          findBySlug(user?.slug as string);
          onFavoriteToggle?.();
        })
        .catch(() => {});
    } else {
      axios
        .post(ApiBaseUrl + "/api/favoris/", fd, config)
        .then(() => {
          findBySlug(user?.slug as string);
          onFavoriteToggle?.();
        })
        .catch(() => {});
    }
  };

  const isFavorite = (id: number | string): boolean =>
    !!favorisData?.find((x) => x?.produit?.id == id);

  const onToggleFavorite = (id: number | string) => {
    if (!user?.slug) {
      Swal.fire({
        icon: "info",
        title: "Veuillez vous connecter pour ajouter un produit aux favoris.",
        iconColor: Color.success,
        showConfirmButton: false,
        timer: 6000,
      });
    } else {
      handleAddFavoris(id);
    }
  };

  type PromotionWithActive = { taux?: number; is_active?: boolean };
  const catPromo = item?.categorie?.promotion as PromotionWithActive | undefined;
  const subCatPromo = item?.sous_categorie?.promotion as PromotionWithActive | undefined;

  const reductionTaux = catPromo?.taux ?? subCatPromo?.taux ?? 0;
  const hasPromotion =
    !!(catPromo?.is_active || subCatPromo?.is_active) || reductionTaux > 0;

  const prixFinal = hasPromotion
    ? item.prix - (item.prix * reductionTaux) / 100
    : item.prix;

  const variationsList = Array.isArray(item?.variations) ? item.variations : [];
  const cartVariations: { id?: string; quantite?: number }[] =
    variationsList.length > 0
      ? [
          variationsList.find((v: { quantite?: number }) => (v?.quantite ?? 0) > 0) ||
            variationsList[0],
        ].filter(Boolean)
      : [];

  const isInCart = (): boolean =>
    (cartItems?.some(
      (cartItem: CartItemType) =>
        cartItem?.produit?.id === item?.id &&
        (cartItem?.variations?.[0]?.id ?? null) === (cartVariations?.[0]?.id ?? null)
    ) ?? false);

  const getCartItemForProduct = (): CartItemType | undefined =>
    cartItems?.find(
      (cartItem: CartItemType) =>
        cartItem?.produit?.id === item?.id &&
        (cartItem?.variations?.[0]?.id ?? null) === (cartVariations?.[0]?.id ?? null)
    );

  const handleToggleCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const stockNum = Number(item?.stock);
    if (stockNum <= 0) return;

    if (isInCart()) {
      const cartItem = getCartItemForProduct();
      if (cartItem) {
        dispatch(removeCartItem(cartItem));
        dispatch(getCartCount());
        dispatch(getTotalAmount());
        Swal.fire({
          icon: "info",
          iconColor: Color.success,
          title: "Produit retiré du panier",
          showConfirmButton: false,
          timer: 2000,
        });
      }
      return;
    }

    const vendeurId =
      typeof item?.vendeur === "object" && item?.vendeur !== null && "id" in item.vendeur
        ? Number((item.vendeur as { id?: string | number }).id) || 0
        : 0;

    const produit: CartItemType = {
      produit: item,
      prix: prixFinal,
      quantite: 1,
      stock: typeof item?.stock === "number" ? item.stock : Number(item?.stock) || 0,
      variations: cartVariations,
      vendeur: vendeurId,
    };
    dispatch(addCartProduct(produit));
    dispatch(getCartCount());
    dispatch(getTotalAmount());
    Swal.fire({
      icon: "success",
      iconColor: Color.success,
      title: "Produit ajouté au panier avec succès !",
      showConfirmButton: false,
      timer: 2000,
    });
  };

  const stockNum = Number(item?.stock);
  const inCart = isInCart();
  const slug = item?.slug ?? "";
  const nom = item?.nom ?? "";

  return (
    <div className="meilleurs-produits-card">
      <div className="meilleurs-produits-card-inner">
        <NavLink
          to={`/produit/${slug}`}
          state={item}
          className="meilleurs-produits-card-image-link"
        >
          <div className="meilleurs-produits-card-image-wrapper">
            <img
              loading="lazy"
              src={imgProd ? getImage(imgProd) : Empty}
              alt={nom}
              className="meilleurs-produits-card-image"
            />
          </div>
        </NavLink>
        <div className="meilleurs-produits-card-content">
          <NavLink to={`/produit/${slug}`} state={item} className="meilleurs-produits-card-content-link">
            <h3 className="meilleurs-produits-card-name">{nom}</h3>
            <p className="meilleurs-produits-card-price">
              <span className="meilleurs-produits-card-price-label">Prix : </span>
              <span className="meilleurs-produits-card-price-value">
                {formatCurrency(prixFinal, devise)}
              </span>
            </p>
          </NavLink>
          <div className="meilleurs-produits-card-actions">
            {stockNum > 0 ? (
              <>
                <NavLink
                  to={`/produit/${slug}`}
                  className="meilleurs-produits-card-btn-primary"
                  onClick={(e) => e.stopPropagation()}
                >
                  Acheter maintenant
                </NavLink>
                <button
                  type="button"
                  className={`meilleurs-produits-card-btn-secondary ${inCart ? "in-cart" : ""}`}
                  onClick={handleToggleCart}
                  aria-label={inCart ? "Retirer du panier" : "Ajouter au panier"}
                >
                  {inCart ? <BsCartCheck /> : <FiShoppingCart />}
                  <span>Ajouter au panier</span>
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="meilleurs-produits-card-btn-primary meilleurs-produits-card-btn-disabled"
                  disabled
                >
                  Acheter maintenant
                </button>
                <button
                  type="button"
                  className="meilleurs-produits-card-btn-secondary meilleurs-produits-card-btn-disabled"
                  disabled
                  aria-label="Rupture de stock"
                >
                  <FiShoppingCart />
                  <span>Ajouter au panier</span>
                </button>
              </>
            )}
            <button
              type="button"
              disabled={isLoading}
              className="meilleurs-produits-card-favorite"
              aria-label="Ajouter aux favoris"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleFavorite(item?.id);
              }}
            >
              {isFavorite(item?.id as string) ? (
                <AiFillHeart className="meilleurs-produits-card-favorite-icon filled" />
              ) : (
                <AiOutlineHeart className="meilleurs-produits-card-favorite-icon" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
